import React, { useCallback, useEffect, useState } from 'react';
import { readReviewableSkills, reviewProtectionSkill } from '../../services/protectBuild';
import { LABEL, NOTE } from './typeScale';

/**
 * How each protected skill runs, decided before the agent may act.
 *
 * <h3>The gap this closes</h3>
 *
 * <p>The Decide → Act gate needs at least one protection skill reviewed and not
 * blocked — `PhaseGateService.hasNonBlockedReviewedSkill`, which
 * `ExternalAgentLifecycleService:249` enforces. Nothing in this walk ever
 * reviewed one, so an agent built here could not reach Act, and without Act the
 * Execution-Ready gate's `phase_act` check could never pass.</p>
 *
 * <h3>Why the options are not the same for every skill</h3>
 *
 * <p>`SkillReviewService:56-63` refuses an autonomous decision when the score
 * is under 0.90. Offering it anyway would hand somebody an error message where
 * an explanation belongs, so it is offered on the skills that can take it and
 * the bar is stated on the ones that cannot.</p>
 *
 * <h3>Why a skill in build is named rather than hidden</h3>
 *
 * <p>A review on a build-phase skill is refused outright
 * (`SkillReviewService:50`), and what such a skill is waiting for is the
 * measuring step, not a decision here. Dropping it silently would leave
 * somebody looking for a skill they know the agent has.</p>
 */
const DECISIONS = [
  { value: 'autonomous', label: 'It runs on its own',
    note: 'No per-call approval. Every call is still checked at the boundary.',
    needsBar: true },
  { value: 'human_approval', label: 'A person approves each call',
    note: 'Each call surfaces for approval before it runs.', needsBar: false },
  { value: 'blocked', label: 'Hold it back',
    note: 'It stays unavailable. At least one skill must not be held back for the '
        + 'agent to act at all.', needsBar: false },
];

const JUSTIFICATION = 'Reviewed while protecting this agent.';

export default function SkillReviewAsk({ agentId, onAnswer, busy = false }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(null);
  const [problem, setProblem] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setState(await readReviewableSkills(agentId));
    setLoading(false);
  }, [agentId]);
  useEffect(() => { load(); }, [load]);

  const decide = async (skillId, decision) => {
    setWorking(`${skillId}:${decision}`);
    setProblem(null);
    try {
      const res = await reviewProtectionSkill(agentId, skillId, decision, JUSTIFICATION);
      if (!res || res.ok === false) {
        setProblem(res?.error || 'The platform would not record this review.');
        return;
      }
      /* Re-read rather than patch: the platform sets the skill's own review
         status and autonomy, and the gate reads those, not this screen's idea
         of them. */
      await load();
    } finally {
      setWorking(null);
    }
  };

  if (loading) return <p style={NOTE}>Reading its protected skills…</p>;

  if (state?.error) {
    return (
      <div style={COL}>
        <p style={{ ...NOTE, color: 'var(--p7-deny-ink)' }}>{state.error}</p>
        <div><button type="button" className="p7-btn" onClick={load}>Try again</button></div>
      </div>
    );
  }

  const skills = state?.skills || [];
  const reviewedCount = skills.filter((s) => s.reviewed).length;
  /* The gate's own predicate, not "something was reviewed": the Decide → Act
     advance needs one review that is NOT blocked. Letting a walk past with
     everything held back would move the refusal two steps on, where the cause
     would read as a gate check rather than the decision that caused it. */
  const cleared = state?.anyCleared === true;

  return (
    <div style={COL}>
      {skills.length === 0 ? (
        <div style={PANEL}>
          <p style={{ ...NOTE, margin: 0 }}>
            {state?.waiting
              ? `${state.waiting} protected skill${state.waiting === 1 ? '' : 's'} `
                + 'still waiting to be measured. A skill is reviewable once it has been '
                + 'measured, so go back and measure them first.'
              : 'This agent has no protected skills to review yet.'}
          </p>
        </div>
      ) : null}

      {skills.map((sk) => (
        <div key={sk.id} style={PANEL}>
          <p style={{ ...LABEL, margin: 0 }}>{sk.name}</p>
          <p style={{ ...NOTE, marginTop: '3px' }}>
            {sk.score === null ? 'Not measured' : `Scored ${Math.round(sk.score * 100)}%`}
            {sk.reviewed ? ' · reviewed' : ''}
          </p>
          {sk.reviewed ? null : (
            <div style={ROW}>
              {DECISIONS.filter((d) => !d.needsBar || sk.canBeAutonomous).map((d) => (
                <button key={d.value} type="button" className="p7-btn"
                        disabled={busy || working !== null}
                        title={d.note}
                        onClick={() => decide(sk.id, d.value)}>
                  {working === `${sk.id}:${d.value}` ? 'Recording…' : d.label}
                </button>
              ))}
            </div>
          )}
          {!sk.reviewed && !sk.canBeAutonomous ? (
            <p style={{ ...NOTE, marginTop: '6px' }}>
              Running on its own needs a score of 90% or more, so it is not offered here.
            </p>
          ) : null}
        </div>
      ))}

      {state?.waiting && skills.length ? (
        <p style={NOTE}>
          {state.waiting} more still waiting to be measured.
        </p>
      ) : null}

      {problem ? (
        <p style={{ ...NOTE, color: 'var(--p7-deny-ink)' }}>{problem}</p>
      ) : null}

      {reviewedCount > 0 && !cleared ? (
        <p style={{ ...NOTE, color: 'var(--p7-deny-ink)' }}>
          Every protected skill was held back, so this agent has nothing it may do.
          Let at least one of them run — on its own, or with a person approving — before
          it can act at all.
        </p>
      ) : null}

      <div>
        <button type="button" className="p7-btn p7-btn--primary"
                disabled={busy || working !== null || !cleared}
                onClick={() => onAnswer({ reviewed: reviewedCount })}>
          Continue
        </button>
      </div>
    </div>
  );
}

const COL = { display: 'flex', flexDirection: 'column', gap: 'var(--p7-space-3)' };
const ROW = { display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' };
const PANEL = {
  border: '1px solid var(--p7-line)', borderRadius: 'var(--p7-radius-control)',
  padding: '12px 13px', background: 'var(--p7-surface-sunken)',
};
